// ============================================================
//  SettingsPanel — right panel powered by craft.related.settings
// ============================================================

import React, { useEffect } from 'react';
import { useEditor } from '@craftjs/core';
import type { HeaderState } from '../../../types/index';
import { ALL_FONTS } from '../utils/typography';
import { useImageUpload } from '../hooks/useImageUpload';

// ── Header Settings ───────────────────────────────────────
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      <div className="field-color-wrap">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          className="field-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function HeaderSettings({
  header,
  onUpdate,
}: {
  header: HeaderState;
  onUpdate: (p: Partial<HeaderState>) => void;
}): React.ReactElement {
  const { uploadImage, isUploading, error } = useImageUpload();
  const s = <K extends keyof HeaderState>(k: K, v: HeaderState[K]): void => {
    const patch: Partial<HeaderState> = {};
    patch[k] = v;
    onUpdate(patch);
  };

  const handleLogoUpload = async (file: File): Promise<void> => {
    try {
      const imageUrl = await uploadImage(file);
      s('logoImageSrc', imageUrl);
    } catch (err) {
      console.error('Failed to upload logo:', err);
    }
  };

  const handleBackgroundUpload = async (file: File): Promise<void> => {
    try {
      const imageUrl = await uploadImage(file);
      s('bgImageSrc', imageUrl);
    } catch (err) {
      console.error('Failed to upload background image:', err);
    }
  };

  return (
    <>
      <div className="panel-section">
        <div className="panel-section-title">Logo</div>
        <div className="field-group">
          <label className="field-label">Type</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className={`btn-sm ${header.logoType === 'text' ? 'active' : ''}`}
              onClick={() => s('logoType', 'text')}
            >
              Texte
            </button>
            <button
              className={`btn-sm ${
                header.logoType === 'image' ? 'active' : ''
              }`}
              onClick={() => s('logoType', 'image')}
            >
              Image
            </button>
          </div>
        </div>
        <div className="field-group">
          <label className="field-label">Texte du logo</label>
          <input
            className="field-input"
            value={header.logoText}
            onChange={(e) => s('logoText', e.target.value)}
          />
        </div>
        {header.logoType === 'image' && (
          <>
            <div className="field-group">
              <label className="field-label">URL du logo</label>
              <input
                className="field-input"
                value={header.logoImageSrc}
                placeholder="https://..."
                onChange={(e) => s('logoImageSrc', e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label">Ou charger un fichier logo</label>
              <input
                type="file"
                accept="image/*"
                style={{ fontSize: 11 }}
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  handleLogoUpload(file);
                  // Clear input to allow re-uploading same file
                  e.target.value = '';
                }}
              />
              {isUploading && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  Téléchargement en cours...
                </div>
              )}
              {error && (
                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Erreur: {error}
                </div>
              )}
            </div>
            {header.logoImageSrc && (
              <div style={{ marginTop: 8, position: 'relative' }}>
                <img
                  src={header.logoImageSrc}
                  alt="Logo preview"
                  style={{
                    width: '100%',
                    maxHeight: 80,
                    objectFit: 'contain',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                  }}
                />
                <button
                  onClick={() => s('logoImageSrc', '')}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 20,
                    height: 20,
                    border: 'none',
                    background: 'rgba(220,38,38,0.9)',
                    color: 'white',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Supprimer le logo"
                >
                  ✕
                </button>
              </div>
            )}
            <div className="field-group">
              <label className="field-label">Ajustement du logo</label>
              <select
                className="field-input"
                value={header.logoObjectFit}
                onChange={(e) => s('logoObjectFit', e.target.value as any)}
              >
                <option value="contain">Contenir (contain)</option>
                <option value="cover">Couvrir (cover)</option>
                <option value="fill">Remplir (fill)</option>
                <option value="scale-down">Réduire (scale-down)</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="panel-section">
        <div className="panel-section-title">Typographie</div>
        <div className="field-group">
          <label className="field-label">Police</label>
          <select
            className="field-input"
            value={header.fontFamily}
            onChange={(e) => s('fontFamily', e.target.value)}
          >
            {ALL_FONTS.map((font) => (
              <option key={font.value} value={font.value}>
                {font.name}{' '}
                {font.category === 'google' ? '(Google)' : '(System)'}
              </option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <label className="field-label">Taille (px)</label>
          <input
            className="field-input"
            type="number"
            value={header.fontSize}
            min={12}
            max={72}
            onChange={(e) => s('fontSize', e.target.value)}
          />
        </div>
        <div className="field-group">
          <label className="field-label">Épaisseur</label>
          <select
            className="field-input"
            value={header.fontWeight}
            onChange={(e) => s('fontWeight', e.target.value)}
          >
            <option value="300">Light (300)</option>
            <option value="400">Normal (400)</option>
            <option value="500">Medium (500)</option>
            <option value="600">Semi-bold (600)</option>
            <option value="700">Bold (700)</option>
            <option value="800">Extra-bold (800)</option>
          </select>
        </div>
        <div className="field-group">
          <label className="field-label">Alignement</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['left', 'center', 'right'] as const).map((align) => (
              <button
                key={align}
                className={`btn-sm ${
                  header.textAlign === align ? 'active' : ''
                }`}
                onClick={() => s('textAlign', align)}
                style={{ flex: 1 }}
              >
                {align === 'left' ? '⬅️' : align === 'center' ? '↔️' : '➡️'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="panel-section">
        <div className="panel-section-title">Couleurs & Fond</div>
        <ColorField
          label="Couleur de fond"
          value={header.bgColor}
          onChange={(v) => s('bgColor', v)}
        />
        <div className="field-group">
          <label className="field-label">Image de fond (URL)</label>
          <input
            className="field-input"
            value={header.bgImageSrc}
            placeholder="https://..."
            onChange={(e) => s('bgImageSrc', e.target.value)}
          />
        </div>
        <div className="field-group">
          <label className="field-label">Ou charger une image de fond</label>
          <input
            type="file"
            accept="image/*"
            style={{ fontSize: 11 }}
            disabled={isUploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              handleBackgroundUpload(file);
              // Clear input to allow re-uploading same file
              e.target.value = '';
            }}
          />
          {isUploading && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              Téléchargement en cours...
            </div>
          )}
          {error && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              Erreur: {error}
            </div>
          )}
        </div>
        {header.bgImageSrc && (
          <div style={{ marginTop: 8, position: 'relative' }}>
            <img
              src={header.bgImageSrc}
              alt="Background preview"
              style={{
                width: '100%',
                maxHeight: 80,
                objectFit: 'cover',
                border: '1px solid var(--border)',
                borderRadius: 4,
              }}
            />
            <button
              onClick={() => s('bgImageSrc', '')}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 20,
                height: 20,
                border: 'none',
                background: 'rgba(220,38,38,0.9)',
                color: 'white',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Supprimer l'image de fond"
            >
              ✕
            </button>
          </div>
        )}
        {header.bgImageSrc && (
          <>
            <div className="field-group">
              <label className="field-label">Taille de l&apos;image</label>
              <select
                className="field-input"
                value={header.bgSize}
                onChange={(e) => s('bgSize', e.target.value as any)}
              >
                <option value="cover">Couvrir (cover)</option>
                <option value="contain">Contenir (contain)</option>
                <option value="auto">Automatique (auto)</option>
                <option value="100%">100%</option>
              </select>
            </div>
            <div className="field-group">
              <label className="field-label">Position de l&apos;image</label>
              <select
                className="field-input"
                value={header.bgPosition}
                onChange={(e) => s('bgPosition', e.target.value as any)}
              >
                <option value="center">Centre</option>
                <option value="top">Haut</option>
                <option value="bottom">Bas</option>
                <option value="left">Gauche</option>
                <option value="right">Droite</option>
              </select>
            </div>
            <div className="field-group">
              <label className="field-label">Répétition de l&apos;image</label>
              <select
                className="field-input"
                value={header.bgRepeat}
                onChange={(e) => s('bgRepeat', e.target.value as any)}
              >
                <option value="no-repeat">Aucune</option>
                <option value="repeat">Répéter</option>
                <option value="repeat-x">Répéter horizontalement</option>
                <option value="repeat-y">Répéter verticalement</option>
              </select>
            </div>
          </>
        )}
        <ColorField
          label="Couleur du texte"
          value={header.textColor}
          onChange={(v) => s('textColor', v)}
        />
      </div>

      <div className="panel-section">
        <div className="panel-section-title">Dimensions</div>
        <div className="field-group">
          <label className="field-label">Hauteur (px)</label>
          <input
            className="field-input"
            type="number"
            value={header.height}
            min={40}
            max={120}
            onChange={(e) => s('height', e.target.value)}
          />
        </div>
        <div className="toggle-row">
          <span className="toggle-label">Ombre portée</span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={header.shadow}
              onChange={() => s('shadow', !header.shadow)}
            />
            <span className="toggle-slider" />
          </label>
        </div>
      </div>
    </>
  );
}

// ── Main SettingsPanel ────────────────────────────────────
interface Props {
  selectedHeader: boolean;
  header: HeaderState;
  onUpdateHeader: (p: Partial<HeaderState>) => void;
  /** When true, renders without its own <aside> wrapper (used inside RightPanel) */
  inline?: boolean;
}

export function SettingsPanel({
  selectedHeader,
  header,
  onUpdateHeader,
  inline = false,
}: Props): React.ReactElement {
  const { selected, actions, query } = useEditor((state, query) => {
    const currentNodeId = state.events.selected
      ? Array.from(state.events.selected)[0]
      : null;

    let selected: {
      id: string;
      name: string;
      settings: React.ComponentType | null;
      isDeletable: boolean;
      isDuplicable: boolean;
    } | null = null;

    if (currentNodeId) {
      const node = state.nodes[currentNodeId];
      selected = {
        id: currentNodeId,
        name: node.data.displayName || node.data.name || 'Bloc',
        settings:
          (node.related?.settings as React.ComponentType | undefined) ?? null,
        isDeletable: query.node(currentNodeId).isDeletable(),
        isDuplicable: true,
      };
    }

    return { selected };
  });

  // Apply highlighting to the currently selected element
  useEffect(() => {
    // Remove any existing highlighting
    document.querySelectorAll('.craft-selected-highlight').forEach((el) => {
      el.classList.remove('craft-selected-highlight');
    });

    // Add highlighting to current selection (if not header)
    if (selected && !selectedHeader) {
      const nodeId = selected.id;

      // Try to get the DOM element directly from Craft.js first
      try {
        const craftElement = query.node(nodeId).get().dom;
        if (craftElement) {
          craftElement.classList.add('craft-selected-highlight');
          return; // Successfully highlighted via Craft.js
        }
      } catch (error) {
        console.log(
          'Could not get element via Craft.js query, trying selectors'
        );
      }

      // Fallback to selector-based approach
      const selectors = [
        `[data-node-id="${nodeId}"]`,
        `div[data-node-id="${nodeId}"]`,
        `[data-craftjs="${nodeId}"]`,
        `div[data-craftjs="${nodeId}"]`,
        `[data-node="${nodeId}"]`,
        `[data-craftjs-node-id="${nodeId}"]`,
      ];

      let found = false;
      for (const selector of selectors) {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          elements.forEach((element) => {
            element.classList.add('craft-selected-highlight');
          });
          found = true;
          break;
        }
      }

      if (!found) {
        console.log(
          'SettingsPanel: Element not found for highlighting:',
          nodeId
        );
      }
    }

    // Cleanup function
    return () => {
      document.querySelectorAll('.craft-selected-highlight').forEach((el) => {
        el.classList.remove('craft-selected-highlight');
      });
    };
  }, [selected, selectedHeader, query]);

  const panelStyle: React.CSSProperties = inline
    ? { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }
    : {
        width: 300,
        background: 'var(--surface)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      };

  const Wrapper = inline ? 'div' : 'aside';

  const headerStyle: React.CSSProperties = {
    padding: '14px 16px 12px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  };

  const badgeStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    background: 'var(--accent-dim)',
    padding: '3px 8px',
    borderRadius: 4,
  };

  // ── Header selected ───────────────────────────────────
  if (selectedHeader) {
    return (
      <Wrapper style={panelStyle}>
        <div style={headerStyle}>
          <span style={badgeStyle}>Global</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Header commun</span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          <HeaderSettings header={header} onUpdate={onUpdateHeader} />
        </div>
      </Wrapper>
    );
  }

  // ── Block selected ────────────────────────────────────
  if (selected) {
    return (
      <Wrapper style={panelStyle}>
        <div style={headerStyle}>
          <span style={badgeStyle}>Bloc</span>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
            {selected.name}
          </span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {selected.settings && React.createElement(selected.settings)}
        </div>

        <div
          style={{
            padding: '12px 14px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 8,
          }}
        >
          {selected?.isDuplicable && (
            <button
              className="btn btn-outline"
              style={{ flex: 1 }}
              onClick={() => {
                if (!selected) return;

                const nodeTree = query.node(selected.id).toNodeTree();

                // 🔥 Step 1: Create ID map
                const idMap: Record<string, string> = {};
                Object.keys(nodeTree.nodes).forEach((nodeId) => {
                  idMap[nodeId] = Math.random().toString(36).substr(2, 9);
                });

                // 🔥 Step 2: Rebuild nodes with new IDs
                const newNodes: any = {};

                Object.keys(nodeTree.nodes).forEach((nodeId) => {
                  const node = nodeTree.nodes[nodeId];
                  const newId = idMap[nodeId];

                  newNodes[newId] = {
                    ...node,
                    id: newId,
                    data: {
                      ...node.data,
                      parent: node.data.parent
                        ? idMap[node.data.parent]
                        : node.data.parent,
                      nodes:
                        node.data.nodes?.map((id: string) => idMap[id]) || [],
                      linkedNodes: Object.fromEntries(
                        Object.entries(node.data.linkedNodes || {}).map(
                          ([k, v]) => [k, idMap[v as string]]
                        )
                      ),
                    },
                  };
                });

                // 🔥 Step 3: Create fresh node tree
                const freshNodeTree = {
                  rootNodeId: idMap[nodeTree.rootNodeId],
                  nodes: newNodes,
                };

                const parentId =
                  query.node(selected.id).get().data.parent ?? 'ROOT';

                // 🔥 Step 4: Add duplicated node
                actions.addNodeTree(freshNodeTree, parentId);
              }}
            >
              ⧉ Dupliquer
            </button>
          )}

          {selected?.isDeletable && selected.id !== 'ROOT' && (
            <button
              className="btn btn-danger"
              onClick={() => {
                if (!selected) return;

                try {
                  actions.delete(selected.id);
                } catch (error) {
                  console.error('Delete failed:', error);
                }
              }}
            >
              ✕ Supprimer
            </button>
          )}
        </div>
      </Wrapper>
    );
  }

  // ── Nothing selected ──────────────────────────────────
  return (
    <Wrapper style={panelStyle}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          color: 'var(--muted)',
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 36, opacity: 0.4 }}>🖱</div>
        <p style={{ fontSize: 12, lineHeight: 1.6 }}>
          Cliquez sur un bloc pour éditer ses propriétés
        </p>
        <div style={{ marginTop: 16, width: '100%' }}>
          <div className="panel-section-title">Raccourcis</div>
          {[
            ['Annuler', 'Ctrl+Z'],
            ['Supprimer', 'Del'],
            ['Déselectionner', 'Esc'],
          ].map(([a, k]) => (
            <div
              key={a}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                padding: '5px 0',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ color: 'var(--muted)' }}>{a}</span>
              <kbd
                style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 10,
                }}
              >
                {k}
              </kbd>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}
